
import { useState } from "react";
import { EmailLog, useEmailLogs } from "@/hooks/useEmailLogs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { format } from "date-fns";
import { Loader2, RefreshCw, Search, X } from "lucide-react";

// Status badge component
const StatusBadge = ({ status }: { status: string }) => {
  const getStatusColor = () => {
    switch (status) {
      case 'sent':
        return "bg-green-100 text-green-800";
      case 'delivered':
        return "bg-green-100 text-green-800";
      case 'opened':
        return "bg-blue-100 text-blue-800";
      case 'clicked':
        return "bg-indigo-100 text-indigo-800";
      case 'error':
        return "bg-red-100 text-red-800";
      case 'bounced':
        return "bg-red-100 text-red-800";
      case 'delivery_delayed':
        return "bg-yellow-100 text-yellow-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor()}`}>
      {status}
    </span>
  );
};

const EmailLogsTable = () => {
  const [filter, setFilter] = useState({
    status: 'all',
    search: '',
  });
  const { data, isLoading, error, refetch, resendEmail, isResending } = useEmailLogs(filter);

  // Handle filter changes
  const handleFilterChange = (key: string, value: string) => {
    setFilter(prev => ({ ...prev, [key]: value }));
  };

  // Clear search filter
  const clearSearch = () => {
    handleFilterChange('search', '');
  };

  // Format date for display
  const formatDate = (dateString: string) => {
    try {
      return format(new Date(dateString), 'MMM d, yyyy h:mm a');
    } catch (e) {
      return 'Invalid date';
    }
  };

  // Handle resend email
  const handleResend = (emailLog: EmailLog) => {
    resendEmail(emailLog);
  };

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <CardTitle>Email Logs</CardTitle>
          <Button
            onClick={() => refetch()}
            variant="outline"
            size="sm"
            className="flex items-center gap-2"
          >
            <RefreshCw className="h-4 w-4" />
            Refresh
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {/* Filter controls */}
        <div className="mb-4 flex flex-col sm:flex-row gap-2">
          <div className="flex-grow relative">
            <Input
              placeholder="Search by email or subject..."
              value={filter.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              className="pr-8"
            />
            {filter.search && (
              <button
                onClick={clearSearch}
                className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
          <div className="w-full sm:w-40">
            <Select
              value={filter.status}
              onValueChange={(value) => handleFilterChange('status', value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="sent">Sent</SelectItem>
                <SelectItem value="delivered">Delivered</SelectItem>
                <SelectItem value="opened">Opened</SelectItem>
                <SelectItem value="clicked">Clicked</SelectItem>
                <SelectItem value="error">Error</SelectItem>
                <SelectItem value="bounced">Bounced</SelectItem>
                <SelectItem value="delivery_delayed">Delayed</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Error message */}
        {error && (
          <div className="bg-red-50 text-red-800 p-4 rounded-md mb-4">
            Failed to load email logs: {(error as Error).message}
          </div>
        )}

        {/* Loading state */}
        {isLoading ? (
          <div className="flex justify-center items-center h-40">
            <Loader2 className="h-8 w-8 animate-spin text-gray-500" />
          </div>
        ) : data && data.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="px-4 py-2 text-left">To</th>
                  <th className="px-4 py-2 text-left">Subject</th>
                  <th className="px-4 py-2 text-left">Status</th>
                  <th className="px-4 py-2 text-left">Sent</th>
                  <th className="px-4 py-2 text-left">Updated</th>
                  <th className="px-4 py-2 text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {data.map((log) => (
                  <tr key={log.id} className="border-b hover:bg-gray-50">
                    <td className="px-4 py-3">{log.to_email}</td>
                    <td className="px-4 py-3">{log.subject}</td>
                    <td className="px-4 py-3">
                      <StatusBadge status={log.status} />
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">{formatDate(log.created_at)}</td>
                    <td className="px-4 py-3 whitespace-nowrap">{log.last_updated_at ? formatDate(log.last_updated_at) : '-'}</td>
                    <td className="px-4 py-3 text-center">
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => handleResend(log)}
                        disabled={isResending}
                      >
                        {isResending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                        Retry
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center h-40 text-gray-500">
            <Search className="h-12 w-12 mb-2 opacity-20" />
            <p>No email logs found</p>
            <p className="text-sm">Try adjusting your filters or send some emails first</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default EmailLogsTable;
